import { levelAt, mapInfo, worldToRadar, type Level, type MapId } from "@69ly/maps";
import {
  kindClock,
  planLookup,
  schedulePlan,
  type Combo,
  type ExecutionKind,
  type Plan,
  type PlanAction,
  type PlanLookup,
  type PlanOpponent,
  type PlanPlayer,
  type Role,
  type Schedule,
  type Stuff,
} from "@69ly/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Short ids for plan items: unique enough within one plan. */
export const planId = (prefix = "") => `${prefix}${Math.random().toString(36).slice(2, 9)}`;

/** A player of the plan or an opponent: the same moves, holds and calls. */
export type PlanActor = PlanPlayer | (PlanOpponent & { slot?: undefined; role?: undefined; memberId?: undefined });
export const isPlayer = (a: PlanActor): a is PlanPlayer => "slot" in a && typeof a.slot === "number";

export const DEFAULT_ROLES: Role[] = ["entry", "trade", "smokes", "support", "lurk"];

/** A new plan: the useful window of each kind, nobody placed yet. */
export function emptyPlan(kind: ExecutionKind): Plan {
  const window = kind === "retake" ? { from: kindClock(kind), to: 0 } : { from: 115, to: 60 };
  return { window, bomb: null, bombKnownAt: null, chapters: [], players: [], opponents: [] };
}

/** The schedule of a plan, worked out again when it changes. */
export function usePlanSchedule(plan: Plan, stuffs: Stuff[], combos: Combo[]): { schedule: Schedule; lookup: PlanLookup } {
  const lookup = useMemo(() => planLookup(stuffs, combos), [stuffs, combos]);
  const schedule = useMemo(() => schedulePlan(plan, lookup), [plan, lookup]);
  return { schedule, lookup };
}

/** A radius in world units as a fraction of the radar's size. */
export const radarRadius = (map: MapId, units: number) => units / (mapInfo(map).scale * 1024);

/** The floor a spot is on (z null: the upper one). */
export const floorOf = (map: MapId, z: number | null): Level => (z === null ? "upper" : levelAt(map, z));

/** The angle on screen (degrees, clockwise from the right) of a CS2 yaw at a spot: the radar's own orientation. */
export function screenYaw(map: MapId, at: { x: number; y: number }, yaw: number): number {
  const rad = (yaw * Math.PI) / 180;
  const a = worldToRadar(map, at.x, at.y);
  const b = worldToRadar(map, at.x + Math.cos(rad) * 100, at.y + Math.sin(rad) * 100);
  return (Math.atan2(b.v - a.v, b.u - a.u) * 180) / Math.PI;
}

/** A CS2 yaw from one spot towards another. */
export const yawTowards = (from: { x: number; y: number }, to: { x: number; y: number }) => (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;

/** Where an actor is at the end of its actions so far (or before action `before`): where the next move starts from. */
export function endPoint(actor: PlanActor, lookup: PlanLookup, before?: number): { x: number; y: number; z: number | null } {
  let p: { x: number; y: number; z: number | null } = { x: actor.start.x, y: actor.start.y, z: actor.start.z ?? null };
  const list = (actor.actions as PlanAction[]).slice(0, before ?? actor.actions.length);
  for (const a of list) {
    if (a.kind === "move") p = { x: a.to.x, y: a.to.y, z: a.to.z ?? null };
    if (a.kind === "throw") {
      const th = lookup.stuff(a.stuffId);
      if (th) p = { x: th.x, y: th.y, z: th.z };
    }
    if (a.kind === "combo") {
      const list2 = lookup.combo(a.comboId);
      const th = list2?.[list2.length - 1];
      if (th) p = { x: th.x, y: th.y, z: th.z };
    }
  }
  return p;
}

export const PLAY_SPEEDS = [0.5, 1, 2, 4] as const;
export type PlaySpeed = (typeof PLAY_SPEEDS)[number];

/**
 * The playhead: seconds since the window started, running at a speed, looping
 * a stretch when asked. The frame loop keeps going only while it plays.
 */
export function usePlayhead(length: number) {
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<PlaySpeed>(1);
  const [loop, setLoop] = useState<{ from: number; to: number } | null>(null);
  const ref = useRef(elapsed);
  ref.current = elapsed;

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      let next = ref.current + dt * speed;
      const end = loop ? loop.to : length;
      if (next >= end) {
        if (loop) next = loop.from;
        else {
          ref.current = length;
          setElapsed(length);
          setPlaying(false);
          return;
        }
      }
      ref.current = next;
      setElapsed(next);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, speed, length, loop]);

  const seek = useCallback((e: number) => {
    const v = Math.min(length, Math.max(0, e));
    ref.current = v;
    setElapsed(v);
  }, [length]);
  const play = useCallback(() => {
    if (ref.current >= length - 0.05) seek(loop?.from ?? 0);
    setPlaying(true);
  }, [length, loop, seek]);
  const pause = useCallback(() => setPlaying(false), []);
  const toggle = useCallback(() => (playing ? pause() : play()), [playing, pause, play]);
  return { elapsed, playing, speed, setSpeed, loop, setLoop, seek, play, pause, toggle };
}
export type Playhead = ReturnType<typeof usePlayhead>;

/** The moments worth jumping to: every action's start, throws landing, chapters. */
export function eventTimes(schedule: Schedule, chapters: { at: number }[]): number[] {
  const out = new Set<number>([0]);
  for (const t of schedule.times.values()) out.add(Math.round(t.start * 10) / 10);
  for (const th of schedule.throws) out.add(Math.round(th.lands * 10) / 10);
  for (const c of chapters) out.add(Math.round(schedule.toElapsed(c.at) * 10) / 10);
  return [...out].filter((x) => x >= 0).sort((a, b) => a - b);
}

// ---------------------------------------------------------------- editing a plan (pure)

type AnyAction = PlanAction;

/** The same plan with one actor (player or opponent) changed. */
export function withActor(plan: Plan, id: string, fn: (a: PlanActor) => PlanActor): Plan {
  return {
    ...plan,
    players: plan.players.map((p) => (p.id === id ? (fn(p) as PlanPlayer) : p)),
    opponents: plan.opponents.map((o) => (o.id === id ? (fn(o as PlanActor) as PlanOpponent) : o)),
  };
}

/** The actor an action belongs to. */
export function ownerOf(plan: Plan, actionId: string): PlanActor | null {
  return ([...plan.players, ...plan.opponents] as PlanActor[]).find((a) => (a.actions as AnyAction[]).some((x) => x.id === actionId)) ?? null;
}

/** Adds an action to an actor: after `afterId` when it is theirs, else at the end. */
export function addAction(plan: Plan, actorId: string, action: AnyAction, afterId?: string | null): Plan {
  return withActor(plan, actorId, (a) => {
    const list = [...(a.actions as AnyAction[])];
    const i = afterId ? list.findIndex((x) => x.id === afterId) : -1;
    list.splice(i >= 0 ? i + 1 : list.length, 0, action);
    return { ...a, actions: list } as PlanActor;
  });
}

/** Changes one action, wherever it is. */
export function updateAction(plan: Plan, actionId: string, fn: (a: AnyAction) => AnyAction): Plan {
  const owner = ownerOf(plan, actionId);
  if (!owner) return plan;
  return withActor(plan, owner.id, (a) => ({ ...a, actions: (a.actions as AnyAction[]).map((x) => (x.id === actionId ? fn(x) : x)) }) as PlanActor);
}

/** Links to removed actions are dropped: those actions go back to "right after the one before". */
function dropLinks(plan: Plan, gone: Set<string>): Plan {
  const fix = (a: PlanActor): PlanActor => ({ ...a, actions: (a.actions as AnyAction[]).map((x) => (x.after && gone.has(x.after.ref) ? { ...x, after: null } : x)) }) as PlanActor;
  return { ...plan, players: plan.players.map((p) => fix(p) as PlanPlayer), opponents: plan.opponents.map((o) => fix(o as PlanActor) as PlanOpponent) };
}

export function removeAction(plan: Plan, actionId: string): Plan {
  const owner = ownerOf(plan, actionId);
  if (!owner) return plan;
  const next = withActor(plan, owner.id, (a) => ({ ...a, actions: (a.actions as AnyAction[]).filter((x) => x.id !== actionId) }) as PlanActor);
  return dropLinks(next, new Set([actionId]));
}

/** Moves an action one step earlier (-1) or later (1) in its actor's list. */
export function shiftAction(plan: Plan, actionId: string, dir: -1 | 1): Plan {
  const owner = ownerOf(plan, actionId);
  if (!owner) return plan;
  return withActor(plan, owner.id, (a) => {
    const list = [...(a.actions as AnyAction[])];
    const i = list.findIndex((x) => x.id === actionId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return a;
    [list[i], list[j]] = [list[j], list[i]];
    return { ...a, actions: list } as PlanActor;
  });
}

export function removeActor(plan: Plan, id: string): Plan {
  const actor = ([...plan.players, ...plan.opponents] as PlanActor[]).find((a) => a.id === id);
  if (!actor) return plan;
  const gone = new Set((actor.actions as AnyAction[]).map((x) => x.id));
  return dropLinks({ ...plan, players: plan.players.filter((p) => p.id !== id), opponents: plan.opponents.filter((o) => o.id !== id) }, gone);
}

/** A clock typed by hand: "1:12", "1:12.5", "72" or "0:05". Null when it is not one. */
export function parsePlanClock(value: string): number | null {
  const v = value.trim();
  const m = /^(\d{1,2}):(\d{1,2}(?:\.\d)?)$/.exec(v) ?? /^()(\d{1,3}(?:\.\d)?)$/.exec(v);
  if (!m) return null;
  const seconds = Number(m[1] || 0) * 60 + Number(m[2]);
  return Number.isFinite(seconds) ? Math.round(seconds * 10) / 10 : null;
}
